import { inject, Injectable } from '@angular/core';
import { LocalRepository } from '@shared/helper/local-repository';
import { UserTypeEnum } from '@shared/model/enum/user-type-enum';
import { User } from '@shared/model/user-context/user.model';
import { CustomersService } from '@shared/service/http_services/customer.service';
import { CustomerAggrigatorService } from '@shared/service/http_services/customerAggrigator.service';
import { SigninService } from '@shared/service/http_services/signin.service';
import { ACCESS_TOKEN_KEY, ACCESS_TOKEN_KEY_CLIENT } from 'constants/storage-keys';
import { BehaviorSubject, Observable, switchMap, of, tap, throwError, catchError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})

export class UserStateService {

  private _customerService = inject(CustomersService);
  private _repo = inject(LocalRepository);
  private _signInService = inject(SigninService);
  private _customerAggrigatorService = inject(CustomerAggrigatorService);
  private _userData = new BehaviorSubject<User | null>(null);


  public get userData(): Observable<User | null> {
    return this._userData.asObservable();
  }
  public set userData(d: User | null) {
    this._userData.next(d);
  }

  public get userDataSnapshot(): User | null {
    return this._userData.value;
  }



  public initializeUserState(): Observable<any> {
    return this.getClientToken().pipe(
      switchMap(() => {
        const access_token = this._repo.getCookieItem(ACCESS_TOKEN_KEY);
        if (access_token)
          return this.getUserData();
        else {
          this.resetUserState();
          return of(null);
        }
      }),
      catchError(err => {
        console.log(err);
        return of(null)
      })
    );
  }


  private getClientToken() {
    const client_token = this._repo.getCookieItem(ACCESS_TOKEN_KEY_CLIENT);
    return client_token ? of(client_token) as Observable<any> : this._signInService.signinClient();
  }



  private getUserData(): Observable<any> {
    return this._customerService.getUserData().pipe(
      tap(r => {
        if (!r.hasError)
          this.assignUserObject(r.result);
        else
          throwError(() => of('failed'))
      }),
      switchMap(() => this.analyzeUserProfile()),
      catchError(() => {
        this.resetUserState();
        return of(null);
      })
    );
  }


  private assignUserObject(user: User) {
    const isAuthor = false;
    const isPublisher = false;
    const isAdmin = user.roleNames?.toLowerCase().includes('admin');
    const isLibrarian = user.roleNames?.toLowerCase().includes('librarian');
    const isBlogger = user.roleNames?.toLowerCase().includes('blogger');
    const isSupporter = user.roleNames?.toLowerCase().includes('supporter');
    this._userData.next({ ...user, isAuthor, isAdmin, isPublisher, isLibrarian, isBlogger, isSupporter });
  }


  private analyzeUserProfile() {
    const user = this._userData.value!;
    return this._customerAggrigatorService.transFormProfileId(user?.id, true).pipe(
      tap(res => {
        if (!res.hasError) {
          const userType = res.result?.type;
          const isAuthor = userType === UserTypeEnum.Author ? true : false;
          const isPublisher = userType === UserTypeEnum.Publisher ? true : false;
          this._userData.next({
            ...user,
            isAuthor,
            isPublisher
          });
        }
      }),
      catchError(err => {
        console.log(err);
        return of(null);
      })
    );
  }

  public resetUserState() {
    this._repo.clearUserData();
    this._userData.next(null);
  }

}
